'use client';

import { Button } from '@ui';
import { StyledInput } from '@ui/Form';
import { useClubMemberFields } from 'components/ClubMemberManagement/hooks';
import SelectComponent from 'components/SelectComponent';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';

interface FieldOptionsValues {
    name: string;
    rules: string[];
}

export interface FieldOptionsResult {
    name: string;
    label: string;
    rules: string[];
}

interface FieldOptionsProps {
    nameEditable?: boolean;
    nameLabel?: string;
    buttonLabel: string;
    placeholder?: string;
    availableRules?: string[];
    textarea?: boolean;
    cb: (val: FieldOptionsResult) => void;
    children?: React.ReactNode;
}

const ALL_RULES = [{ value: 'required', name: 'Muss ausgefüllt werden' }];

export const FieldOptions: React.FC<FieldOptionsProps> = ({
    nameEditable = true,
    nameLabel,
    buttonLabel,
    placeholder,
    availableRules,
    textarea = false,
    cb,
    children
}) => {
    const [values, setValues] = useState<FieldOptionsValues>({ name: '', rules: [] });

    const handleAddField = () => {
        if (nameEditable && values.name === '') {
            alert('Du musst einen Titel vergeben!');
            return;
        }
        cb({ name: uuid(), label: values.name, rules: values.rules });
        setValues({ name: '', rules: [] });
    };

    const handleRuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setValues((prev) => {
            const rules = [...prev.rules];
            const idx = rules.indexOf(value);
            if (idx === -1 && checked) rules.push(value);
            else if (idx !== -1 && !checked) rules.splice(idx, 1);
            return { ...prev, rules };
        });
    };

    return (
        <div>
            {nameEditable && (
                <StyledInput
                    label={nameLabel}
                    placeholder={placeholder}
                    name="name"
                    type={textarea ? 'textarea' : 'text'}
                    value={values.name}
                    bordered
                    rounded
                    fullWidth
                    onChange={(e) => setValues((v) => ({ ...v, name: (e.target as HTMLInputElement).value }))}
                />
            )}

            {availableRules && (
                <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">Bedingungen festlegen</label>
                    {ALL_RULES.filter((r) => availableRules.includes(r.value)).map((rule) => (
                        <div key={rule.value} className="tw-flex tw-items-center tw-gap-2">
                            <input
                                className="tw-size-4 tw-cursor-pointer"
                                type="checkbox"
                                value={rule.value}
                                checked={values.rules.includes(rule.value)}
                                onChange={handleRuleChange}
                            />
                            <label className="tw-text-sm">{rule.name}</label>
                        </div>
                    ))}
                </div>
            )}

            {children}

            <div className="tw-mt-3 tw-flex tw-justify-end">
                <Button color="default" onPress={handleAddField}>
                    {buttonLabel}
                </Button>
            </div>
        </div>
    );
};

interface AutoFillSelectProps {
    value: string | undefined;
    onChange: (val: string) => void;
    fields?: { key: string; title: string }[];
    isLoading?: boolean;
    /** Explains both uses of the link below the select; off for all but one select of a group. */
    showHint?: boolean;
}

export const AutoFillSelect: React.FC<AutoFillSelectProps> = ({ value, onChange, fields, isLoading, showHint = true }) => {
    const options = [
        { value: '', label: 'Keine Daten beziehen' },
        { value: 'user.email', label: 'Benutzeraccount - E-Mail Adresse' },
        { value: 'user.phone', label: 'Benutzeraccount - Telefonnummer' },
        { value: 'user.date_of_birth', label: 'Benutzeraccount - Geburtsdatum' },
        { value: 'user.invoice_recipient', label: 'Benutzeraccount - Rechnungsempfänger' },
        { value: 'user.iban', label: 'Benutzeraccount - IBAN' },
        ...(isLoading
            ? [{ value: '__loading', label: 'Mitgliederverwaltung - Lade ...' }]
            : (fields ?? []).map((field) => ({
                  value: `club_members.${field.key}`,
                  label: `Mitgliederverwaltung - ${field.title}`
              })))
    ];

    return (
        <>
            <SelectComponent
                value={isLoading && value === undefined ? '__loading' : (value ?? '')}
                onChange={(selected) => {
                    if (selected === '__loading') return;
                    onChange(String(selected));
                }}
                disabled={isLoading}
                options={options}
                buttonProps={{
                    containerClassName: 'tw-my-0',
                    className: '!tw-justify-between !tw-rounded-xl'
                }}
            />
            {showHint && (
                <p className="tw-mb-0 tw-mt-1 tw-pl-1 tw-text-xs tw-text-accents-6">
                    Füllt das Feld beim Ausfüllen mit diesen Daten vor. Bei „Mitglied erstellen aus Buchung“ wird die
                    Antwort automatisch diesem Mitgliedsfeld zugeordnet.
                </p>
            )}
        </>
    );
};

interface AutoFillOptionsProps {
    cb: (val: string) => void;
    val?: string;
}

export const AutoFillOptions: React.FC<AutoFillOptionsProps> = ({ cb, val }) => {
    const { fields, isLoading } = useClubMemberFields();
    const [autofill, setAutofill] = useState(val ?? '');
    const selectableFields = fields?.filter(
        (field): field is { key: string; title: string } =>
            typeof field.key === 'string' && typeof field.title === 'string'
    );

    return (
        <>
            <div className="tw-mb-4">
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Mit Mitgliedsdaten verknüpfen</label>
                <AutoFillSelect
                    value={autofill}
                    onChange={setAutofill}
                    fields={selectableFields}
                    isLoading={isLoading}
                />
            </div>
            <Button color="default" block onPress={() => cb(autofill)}>
                Speichern
            </Button>
        </>
    );
};
